import React, { useCallback, useMemo } from 'react';
import { useRouteMatch } from 'react-router';
import Page from 'components/Page';
import Table from 'components/Table';
import { Button } from 'antd';
import useDataSource from 'hooks/useDataSource';
import { DeleteFilled, ZoomInOutlined } from '@ant-design/icons';
import { gql } from '@apollo/client';
import { useMutation } from 'hooks/apollo';
import DeviceTypeIcons from 'components/DeviceTypeIcons';
import useConfig from 'hooks/useConfig';
import DateRangePicker from 'components/DateRangePicker';
import { calculateMainDateRange, getDefaultDateRange } from 'shared/constants/date';
import { useLocalStorageState } from 'ahooks';
import dayjs from 'dayjs';

const extraFields = `
  website {
    id
    matomoId
    matomoRecordedPageViews(hsrId: $matomoHsrId, dateRange: $dateRange) {
      id
      matomoVisitId
      serverTime
      deviceType
      duration
      url
      events
    }
  }
`;

const REMOVE_RECORDED_SESSION = gql`
  mutation($websiteId: ID!, $matomoHsrId: Int!, $matomoVisitId: Int!) {
    deleteRecordedSession(websiteId: $websiteId, matomoHsrId: $matomoHsrId, matomoVisitId: $matomoVisitId)
  }
`;

const REMOVE_RECORDED_PAGEVIEW = gql`
  mutation($websiteId: ID!, $matomoHsrId: Int!, $matomoHsrLogId: Int!) {
    deleteRecordedPageview(websiteId: $websiteId, matomoHsrId: $matomoHsrId, matomoHsrLogId: $matomoHsrLogId )
  }
`;

const getDeviceIcon = (deviceType) => {
  switch (deviceType) {
    case 1:
      return <DeviceTypeIcons deviceTypes={['DESKTOP']} />;
    case 2:
      return <DeviceTypeIcons deviceTypes={['TABLET']} />;
    case 3:
      return <DeviceTypeIcons deviceTypes={['MOBILE']} />;
    default:
      return null;
  }
};

const formatUrl = (url) => {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
};
const displayUrl = (url) => {
  const urlObj = new URL(formatUrl(url));
  return urlObj.pathname;
};
const columns = [
  {
    title: 'Entry - Exit URL',
    dataIndex: 'url',
    render: (url, record) => {
      if (record.children?.length > 1) {
        return (
          <>
            {displayUrl(record.url)}
            {' '}
            &rarr;
            {' '}
            {displayUrl(record.exitUrl)}
          </>
        );
      }
      return (
        <a href={formatUrl(url)} target="_blank">{displayUrl(url)}</a>
      );
    },
  },
  {
    title: 'Pageviews',
    dataIndex: 'pageViews',
    type: 'number',
  },
  {
    title: 'Events',
    dataIndex: 'events',
    type: 'number',
  },
  {
    title: 'Date',
    dataIndex: 'serverTime',
    type: 'datetime',
    defaultSortOrder: 'descend',
  },
  {
    title: 'Time on website',
    dataIndex: 'duration',
    type: 'duration',
  },
  {
    title: 'Device',
    dataIndex: 'deviceType',
    render: (deviceType) => getDeviceIcon(deviceType),
    align: 'center',
  },
];

const SessionRecordingTable = ({ sessionRecordings }) => {
  const { config } = useConfig();
  const match = useRouteMatch('/data-sources/:dataSourceId/hsr/session-recordings-new/:matomoHsrId');
  const matomoHsrId = parseInt(match.params.matomoHsrId, 10);
  const [dateRangeConfig, setDateRangeConfig] = useLocalStorageState(`hsr-daterange-${matomoHsrId}`, { defaultValue: getDefaultDateRange() });
  const dateRange = calculateMainDateRange(dayjs, dateRangeConfig);
  const { dataSource, loading, refetch } = useDataSource({ extraFields: extraFields.replace('$matomoHsrId', match.params.matomoHsrId).replace('$dateRange', `{ startDate: "${dateRange.startDate.toISOString()}", endDate: "${dateRange.endDate.toISOString()}"  }`) });
  const [deleteRecordedSession] = useMutation(REMOVE_RECORDED_SESSION);
  const [deleteRecordedPageview] = useMutation(REMOVE_RECORDED_PAGEVIEW);

  const websiteId = dataSource?.website.id;
  const matomoId = dataSource?.website.matomoId;

  const sessionRecording = sessionRecordings.find(s => s.matomoHsrId === matomoHsrId);

  const onRemove = async (record) => {
    if (record.pageViewId) {
      await deleteRecordedSession({ variables: { websiteId, matomoHsrId, matomoVisitId: record.id } });
    } else {
      await deleteRecordedPageview({ variables: { websiteId, matomoHsrId, matomoHsrLogId: record.id } });
    }
    refetch();
  };

  const actionsMenuItems = useCallback((record) => [
    {
      key: 'delete',
      label: 'Delete',
      onClick: () => onRemove(record),
      danger: true,
      icon: <DeleteFilled />,
    },
  ], []);

  const actions = useCallback((record) => [
    <a
      href={`${config?.matomo.baseUrl}?module=HeatmapSessionRecording&action=replayRecording&idSite=${matomoId}&idLogHsr=${record.pageViewId || record.id}&idSiteHsr=${matomoHsrId}`}
      target="_blank"
    >
      <Button icon={<ZoomInOutlined />}>View</Button>
    </a>,
  ], [config, matomoId, matomoHsrId]);

  // group page views by matomoVisitId
  const sessions = useMemo(() => dataSource?.website.matomoRecordedPageViews.reduce((acc, pageView) => {
    const session = acc.find((s) => s.matomoVisitId === pageView.matomoVisitId);
    if (session) {
      session.children.push(pageView);
      session.events += pageView.events;
      session.duration += pageView.duration;
      session.pageViews += 1;
      session.exitUrl = pageView.url;
    } else {
      acc.push({
        ...pageView,
        id: pageView.matomoVisitId,
        children: [pageView],
        pageViewId: pageView.id,
        pageViews: 1,
      });
    }
    return acc;
  }, []).map(session => {
    if (session.children.length === 1) {
      session.children = null;
    }
    return session;
  }), [dataSource]);

  return (
    <Page>
      <Page.Header
        title={`Recorded sessions - ${sessionRecording?.name}`}
        extra={(
          <DateRangePicker
            onChange={setDateRangeConfig}
            value={dateRangeConfig}
            allowCompare={false}
            layout="horizontal"
          />
        )}
      />
      <Page.Content>
        <Table
          columns={columns}
          loading={loading}
          dataSource={sessions}
          actions={actions}
          actionsMenuItems={actionsMenuItems}
        />
      </Page.Content>
    </Page>
  );
};

export default SessionRecordingTable;
